import { DESKTOP, giveColor, MOBILE, TABLET } from 'utils/styles'
import './fonts/fonts.css'

const commonColors = {
  fontColor: (opacity?: number) => giveColor(0, 54, 118, opacity),
  accentColor: (opacity?: number) => giveColor(237, 97, 57, opacity),
  accentFontColor: (opacity?: number) => giveColor(237, 97, 57, opacity),
}

export const textFontSize = {
  desktop: {
    main: '1.1rem',
  },
}

const phoneFallback = {
  background: giveColor(255, 255, 255, 1.0),
  ...commonColors,
}

const projectTheme = {
  palette: {
    white: (opacity?: number) => giveColor(255, 255, 255, opacity),
    black: (opacity?: number) => giveColor(0, 0, 0, opacity),
    secondary: {
      main: 'rgb(237, 97, 57)',
      dark: 'rgba(237, 97, 57, 0.7)',
      light: 'rgba(237, 97, 57, 0.3)',
    },
    primary: {
      main: 'rgba(0, 54, 118, 1)',
      dark: 'rgba(0, 54, 118, 0.7)',
      light: 'rgba(0, 54, 118, 0.3)',
    },
    background: {
      main: '#f9fcfc',
    },
    sectionText: {
      desktop: commonColors,
      phone: phoneFallback,
    },
    sectionBackground: {
      desktop: {
        background: 'transparent',
        backgroundReversed: 'transparent',
        ...commonColors,
      },
      phone: phoneFallback,
    },
    cover: {
      desktop: {
        background: `linear-gradient(to bottom, ${giveColor(
          0,
          0,
          0,
          0.3
        )}, ${giveColor(0, 0, 0, 0.6)})`,
        ...commonColors,
      },
      phone: phoneFallback,
    },
    materials: {
      desktop: {
        background: giveColor(255, 255, 255, 1.0),
        ...commonColors,
      },
      phone: phoneFallback,
    },
    sideElements: {
      desktop: {
        background: 'linear-gradient(to bottom, #000000, rgba(0, 0, 0, 0.9))',
        fontColor: (opacity?: number) => giveColor(255, 255, 255, opacity),
        accentColor: (opacity?: number) => giveColor(237, 97, 57, opacity),
        accentFontColor: (opacity?: number) =>
          giveColor(255, 255, 255, opacity),
      },
      phone: phoneFallback,
    },
    quizzes: {
      desktop: {
        background: 'transparent',
        ...commonColors,
      },
    },
  },
  constants: {
    sizes: {
      desktop: {
        topBarHeight: 80,
        sideBarWidth: 80,
        bottomBarHeight: 80,
        standardButtonSize: 80,
      },
      tablet: {
        topBarHeight: 80,
        sideBarWidth: 80,
        bottomBarHeight: 80,
        standardButtonSize: 80,
      },
      phone: phoneFallback,
    },
  },
  fonts: {
    primary: {
      serif: 'Chivo',
    },
    secondary: {
      serif: 'Chivo',
    },
  },

  overrides: {},
}

const overrides = {
  SectionTextContent: {
    'content-container': {
      padding: '3rem 2% 0 3rem',
    },
  },
  SectionTitle: {
    'title-container': {
      [`@media ${DESKTOP}`]: {
        padding: 0,
        margin: '100px 0 50px',
      },
    },

    title: {
      [`@media ${DESKTOP}`]: {
        textAlign: 'left',
        fontSize: '30px',
        fontWeight: 900,
        lineHeight: 1.2,
      },
    },
  },
  InlineSubcontentParagraph: {
    text: {
      '& p': {
        fontSize: textFontSize.desktop.main,
      },
      '& ol': {
        paddingLeft: 18,
      },
      '& ul': {
        paddingLeft: 18,
        '& li': {
          fontSize: textFontSize.desktop.main,
        },
      },
    },
  },
  Section: {
    subchapter: {
      backgroundColor: projectTheme.palette.background.main,
    },
  },
  TextSupportingMaterial: {
    paragraphs: {
      fontSize: textFontSize.desktop.main,
    },
  },
  InlineSubcontentWidget: {
    container: {
      paddingTop: 20,
      paddingBottom: 20,

      '&::after': {
        content: '""',
        display: 'inline-block',
        height: '100%',
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: -1,
        borderTop: '1px dotted',
        borderBottom: '1px dotted',
        borderColor: '#003676',
        transform: 'scaleY(1.15)',
      },
    },
  },
  ThumbnailWithIcon: {
    icon: {
      display: 'none',
    },
    'thumbnail-cover': {
      backgroundColor: 'transparent !important',
    },
  },
  InteractiveBackground: {
    overlay: {
      backgroundColor: projectTheme.palette.black(0.6),
    },
    'background-placeholder': {
      width: '100%',
      height: '100%',
      background: projectTheme.palette.black(0.6),
    },
  },
  TopBarChapterNumber: {
    'chapter-number': {
      borderBottomRightRadius: 25,
      backgroundColor: projectTheme.palette.primary.main,
    },
    'chapter-number-visible': {
      display: 'none',
    },
  },
  Burger: {
    burger: {
      top: 0,
      right: 0,
      left: 'unset',
      bottom: 'unset',
      height: 'unset',
      '& div': {
        backgroundColor: projectTheme.palette.secondary.main,
        borderBottomLeftRadius: 25,
        backgroundSize: 'unset',

        '&:hover': {
          backgroundSize: 'unset',
        },
      },
    },
  },
  TopBar: {
    'top-dark-overlay': {
      display: 'none',
    },
    'narrow-overlay': {
      display: 'none',
    },
    container: {
      width: `calc(100% - ${projectTheme.constants.sizes.desktop.bottomBarHeight}px)`,
    },
  },
  ScrollSidebar: {
    filler: {
      background: projectTheme.palette.secondary.main,
    },
    'bar-base': {
      backgroundColor: projectTheme.palette.secondary.light,
    },
    'current-section-title': {
      display: 'none',
    },
    'section-title-hidden': {
      height: 0,
    },
    'section-title-active': {
      fontSize: '1.125rem',
      fontWeight: 900,
      lineHeight: 1.11,
      letterSpacing: '0.18px',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: projectTheme.constants.sizes.desktop.bottomBarHeight,
      width: projectTheme.constants.sizes.desktop.sideBarWidth,
      '-webkit-writing-mode': 'vertical-rl',
      writingMode: 'vertical-rl',
      '-moz-transform': 'rotate(180deg)',
      transform: 'rotate(180deg)',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      justifyContent: 'center',
      zIndex: 2,
      color: projectTheme.palette.secondary.main,
    },
    'left-dark-overlay': {
      top: 0,
      height: '100%',
    },
  },
  CrossButton: {
    button: {
      backgroundColor: projectTheme.palette.background.main,
      borderBottomLeftRadius: '35px',
      boxShadow: '-10px 0 40px 0px rgba(0,0,0,0.16)',
    },
    icon: {
      filter:
        'saturate(100%) invert(84%) sepia(92%) saturate(2628%) hue-rotate(199deg) brightness(90%) contrast(105%)',
      transform: 'scale(0.9)',
    },
  },
  StandardButton: {
    overlay: {},
  },
  ChaptersBar: {
    'bar-item': {
      transition: 'all 0.4s',
      color: projectTheme.palette.white(),
      '&:hover': {
        backgroundColor: '',
      },
    },
    'active-bar-item': {
      backgroundColor: '#ed6139',
      borderTopRightRadius: '30px',
      '& $bar': {
        width: '80%',
      },
    },
    bar: {
      width: '59px',
      height: '3px',
      marginTop: '-1.5px',
      backgroundColor: projectTheme.palette.white(),
    },
    'chapter-idx-zero': {
      display: 'none',
    },
    'chapter-idx': {
      fontWeight: 700,
      marginRight: '23px',
      marginLeft: '12px',
      paddingRight: 0,
    },
    'chapter-bar': {
      paddingLeft: 0,
      [`@media ${DESKTOP}`]: {
        paddingLeft: 0,
      },
    },
    'bar-chapter-number': {
      display: 'none',
    },
    text: {
      fontWeight: 700,
    },
  },
  HeaderTitle: {
    title: {
      maxWidth: 450,
      color: projectTheme.palette.primary.main,
      fontWeight: 700,
      textAlign: 'right',
      fontSize: '2.5rem',
      hyphens: 'initial !important',
    },
    'chapter-number': {
      fontSize: '17rem',
      fontWeight: 300,
      transform: 'translate(-140px, -5px)',
      '&::first-letter': {
        color: 'transparent',
      },
    },
  },
  VideoPlayer: {
    'player-wrapper': {
      margin: 0,
      '&&': {
        '& .vjs-control-bar': {
          margin: 0,
          backgroundImage: 'none',
          backgroundColor: projectTheme.palette.background.main,
          color: projectTheme.palette.primary.main,
          borderTopRightRadius: '25px',
          width: '85vw',
          paddingRight: '5vw',
          [`@media ${TABLET}`]: {
            paddingRight: '10vw',
            width: '90vw',
          },
          '& .vjs-progress-control': {
            '& div': {
              height: '0.5px',
            },
            '& .vjs-progress-holder': {
              '& .vjs-play-progress': {
                backgroundColor: projectTheme.palette.primary.main,
              },
              '& .vjs-load-progress': {
                backgroundColor: projectTheme.palette.primary.main,
              },
              '& .vjs-play-progress:before': {
                fontSize: '1.5em',
                top: '-0.5em',
              },
              '& .vjs-load-progress div': {
                background: projectTheme.palette.primary.main,
              },
            },
          },
          '& .vjs-volume-panel .vjs-volume-control div .vjs-volume-level': {
            backgroundColor: projectTheme.palette.primary.main,
          },
          '& .vjs-remaining-time': {
            fontFamily: 'Chivo',
            fontStyle: 'normal',
            lineHeight: 1.33,
            letterSpacing: '0.18px',
          },
          '& .video-meta-container': {
            display: 'flex',
            alignItems: 'center',
            marginLeft: '10px',
            marginRight: '10px',
            fontSize: '18px',
            lineHeight: 1.33,
            letterSpacing: '0.18px',
            // Inner html styling of desc and title
            '& .video-desc-wrapper': {
              display: 'flex',
              flexWrap: 'nowrap',
              flex: 1,
              color: projectTheme.palette.primary.main,
              justifyContent: 'space-between',
              position: 'relative',
            },
            '& .video-desc-textWrapper': {
              padding: '6px 8px 8px 8px',
              maxWidth: '30vw',
            },
            '& .video-desc-title': {
              fontSize: '0.9rem',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',

              fontWeight: 900,
            },
            '& .video-desc-description': {
              fontSize: '1rem',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              maxHeight: '1.5rem',

              fontWeight: 400,
            },
          },
          '& .video-meta-container div .title': {
            fontFamily: 'Chivo',
            fontWeight: 700,
          },
          '& .play-control-custom-right': {
            marginTop: '1em',
          },
        },
        '& .vjs-play-control': {
          '& > span': {
            backgroundColor: projectTheme.palette.white(),
            borderRadius: '50%',
            backgroundSize: '100% !important',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            [`@media ${DESKTOP}`]: {
              left: '80vw',
              backgroundColor: projectTheme.palette.background.main,
            },
          },
        },
      },
    },
  },
  InteractivePanelTitle: {
    title: {
      display: 'none',
    },
  },
  BottomBar: {
    container: {
      maxWidth: '90% !important',

      [`@media ${DESKTOP}`]: {
        paddingLeft: 0,
        width: '90%',
        maxWidth: '90%',
        background: projectTheme.palette.white(),
        paddingRight: 0,
        borderTopRightRadius: 16,
        display: 'flex',
      },
      [`@media ${MOBILE}`]: {
        width: '100% !important',
      },
    },
  },
  GalleryPanel: {
    'panel-content': {
      paddingLeft: 12,
      paddingRight: 12,
      paddingTop: 0,
      background: projectTheme.palette.white(),
      borderTopRightRadius: 16,
    },
  },
  ImageGallery: {
    'title-and-description': {
      // To override css in contentStore. Otherwise it would be needed to import ImageGallery into snb project
      display: 'none !important',
    },
  },
  BottomBarSlider: {
    container: {
      width: '100%',
    },
    'image-buttons-container': {
      [`@media ${MOBILE}`]: {
        gap: '15px',
        padding: '0 10px',
      },
    },
  },
  SliderButton: {
    square: {
      borderRadius: '100%',
      transform: 'scale(1.6)',
    },
    scaled: {
      transform: 'scale(1.6)',
    },
  },
  ImageBackground: {
    'image-description': { display: 'none' },
    'background-image': {
      width: '100%',
      height: '100%',
      position: 'relative',
      zIndex: 1,
      '-webkit-transform': 'translateZ(1)',
      '-webkit-backface-visibility': 'hidden',
    },
    'panel-content': {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: '0 2rem',
    },
  },
  TabsContainer: {
    tabs: {
      display: 'none',
    },
    'top-bar': {
      display: 'none',
    },
  },
  TopBarButtons: {
    container: {
      width: '100%',
      height: '100%',
    },
  },
  LegalButton: {
    'impressum-container': {
      top: 0,
      background: projectTheme.palette.background.main,
    },
    'button-hover-container': {
      display: 'none',
    },
  },
  Quellen: {
    'list-box': {
      display: 'none',
    },
    'chapter-title': {
      fontStyle: 'unset',
      fontWeight: 'bold',
    },
    'point-title': {
      color: projectTheme.palette.secondary.main,
      lineHeight: 1.5,
      letterSpacing: '1.5px',
      fontFamily: projectTheme.fonts.primary.serif,
      fontSize: '15px',
      width: '30%',
      fontWeight: 'bold',

      [`@media ${TABLET}`]: {
        width: '40%',
      },
    },
    'point-container': {
      display: 'flex',
      flexDirection: 'row',
    },
    'point-text': {
      width: '70%',
      lineHeight: 1.5,

      [`@media ${TABLET}`]: {
        width: '60%',
      },
    },
    'quellen-box': {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '40px',
    },
  },
  ImpressumContainer: {
    'content-container': {
      padding: 0,
    },
    'bottom-overlay': {
      display: 'none',
    },
  },
  InteractivePanel: {
    panel: {
      backgroundColor: projectTheme.palette.white(),
      borderTopRightRadius: 16,
    },
  },
  Glossary: {
    'bottom-overlay': {
      display: 'none',
    },
  },
}

projectTheme.overrides = overrides

export default projectTheme
